import React, { useState } from "react";
import { useAppNavigate } from "utils/router";

import {
    ArrowRight,
    ArrowLeft,
    BarChart2,
    Users,
    ChevronDown,
    ChevronUp,
    Bell,
    Gauge,
    LineChart,
    Terminal,
    Wrench,
    Zap,
    InfoIcon,
    Target,
    MessageSquare
} from "lucide-react";
import { SetupCard } from "./SetupCard";
import { InviteTeamModal } from "../Common/InviteTeamModal";
import { Team } from "../Common/types";
import HowProductAnalyticsWork from "../../../assets/images/HowProductAnalyticsWork.gif";
import { SetupHeader } from "../Common/SetupHeader";
import { getStatus } from "../Common/StepsStatusAnalysis";
import { RootState } from "store/redux/store";
import { useSelector } from "react-redux";
import { TeamId } from "store/redux/reducers/team/teamConstants";
import { useDemoMode } from "../../../context/DemoModeContext";

export function SetupPreventChurn() {
    const navigate = useAppNavigate();
    const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<string | null>(null);
    const [expandedCard, setExpandedCard] = useState<string | null>(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState<TeamId | null>(null);
    const { isDemoMode, toggleDemoMode } = useDemoMode();
    const [onset, setOnset] = useState(true);
    const setupCards = [
        {
            id: "retention",
            isEnabled: true,
            beta: false,
            title: "Setup Retention Health Score",
            icon: <Terminal className="w-8 h-8" />,
            shortDesc: "Set up a Retention Health Score to quantify customer loyalty, track engagement trends, and identify early signs of churn",
            challenge: "Need to track product usage but lack proper guidance for instrumentation",
            solution: "Implement comprehensive event tracking with minimal code",
            benefitIcon: <Zap className="w-5 h-5" />,
            benefit: "90% reduction in analytics setup time",
            features: [
                "Page and User activity tracking",
                "Account-level analytics",
                "Billing event monitoring",
                "Automated data validation"
            ],
            teams: ["Customer Success teams"] as Team[],
            setupStatus: getStatus(
                useSelector((state: RootState) => state.modules) as never,
                "product_analytics.setup_product_telemetry"
            ),
            setupTime: 15,
            steps: 8,
            completedSteps: 0
        },
        {
            id: "conversational-intelligence",
            isEnabled: true,
            beta: true,
            addon: true,
            title: "Setup Conversational Intelligence",
            icon: <MessageSquare className="w-8 h-8" />,
            shortDesc: "Connect customer channels, layer AI, and turn every conversation into account-level signals",
            challenge: "Customer conversations live in Slack and tickets but never become structured retention signals",
            solution: "Read shared channels with AI, map them to accounts, and surface risk and intent automatically",
            benefitIcon: <Zap className="w-5 h-5" />,
            benefit: "~15 minute setup with zero engineering",
            features: [
                "Slack workspace and Slack Connect channels",
                "Bring-your-own Anthropic key",
                "Auto-map channels to accounts",
                "Sentiment, tasks, and topic signals"
            ],
            teams: ["Customer Success teams", "Support teams"] as Team[],
            setupStatus: "not_started" as const,
            setupTime: 15,
            steps: 4,
            completedSteps: 0
        },
        {
            id: "expansion",
            isEnabled: false,
            beta: false,
            title: "Setup Expansion",
            icon: <LineChart className="w-8 h-8" />,
            shortDesc: "Create custom reports without SQL",
            challenge: "Need insights but lack technical SQL expertise",
            solution: "Visual report builder with guided analytics",
            benefitIcon: <Wrench className="w-5 h-5" />,
            benefit: "60% faster report creation",
            features: [
                "Visual query builder",
                "Automatic Customer Journey Reports",
                "Customized based on events",
                "Guided setup"
            ],
            teams: ["Product teams", "Marketing teams"] as Team[],
            setupStatus: getStatus(
                useSelector((state: RootState) => state.modules) as never,
                "product_analytics.customize_analytics_reports"
            ),
            setupTime: 10,
            steps: 5,
            completedSteps: 2
        }
    ];

    const handleSelect = (id: string) => {
        setSelectedCard(id);
        setOnset(true);
    };

    const handleExpand = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedCard(expandedCard === id ? null : id);
        setOnset(false);
    };

    const handleInviteTeam = (team: TeamId) => {
        setSelectedTeam(team);
        setShowInviteModal(true);
    };

    const handleContinue = () => {
        if (selectedCard === "retention") {
            navigate("/onboarding/predict-prevent-churn/retention");
        } else if (selectedCard === "conversational-intelligence") {
            navigate("/onboarding/predict-prevent-churn/conversational-intelligence");
        } else if (selectedCard === "expansion") {
            navigate("/onboarding/predict-prevent-churn/expansion");
        }
    };

    function ToogleDemooModeWrapper() {
        toggleDemoMode('/analyze/dashboard');
    }

    return (
        // <div className="h-screen w-screen overflow-y-auto bg-gradient-to-br bg-primaryExtremeLightColor dark:from-gray-900 dark:to-primaryDarkColor transition-colors duration-200">
        <div
            className={
                onset
                    ? "fixed outset-0 h-screen w-screen overflow-y-auto bg-gradient-to-br bg-primaryExtremeLightColor dark:from-gray-900 dark:to-primaryDarkColor transition-colors duration-200"
                    : "h-screen w-screen overflow-y-auto bg-gradient-to-br bg-primaryExtremeLightColor dark:from-gray-900 dark:to-primaryDarkColor transition-colors duration-200"
            }
        >
            <div className="min-h-screen bg-gray-50 w-[70%] mx-auto px-8 relative">
                <div className="flex flex-col py-4 gap-4 mb-7">
                    <div className="flex">
                        <button
                            onClick={() => navigate('/onboarding')}
                            className="w-fit p-0 min-w-0 text-gray-600 hover:text-gray-900">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                    </div>
                    <SetupHeader
                        icon={<BarChart2 className="w-8 h-8 text-primaryColor" />}
                        title="Predict and Prevent Churn"
                        description="Follow these steps to identify at-risk customers early and take proactive actions to reduce churn and improve retention."
                        teams={[TeamId.CUSTOMER_SUCCESS_TEAMS]}
                        setupTime={33}
                        totalSteps={3}
                        completedSteps={0}
                        onInviteTeam={handleInviteTeam}
                    />
                </div>

                {/* How It Works Accordion */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
                    <button
                        onClick={() => setIsHowItWorksOpen(!isHowItWorksOpen)}
                        className="w-full px-6 py-4 flex items-center justify-between border rounded-sm">
                        <div className="flex items-center">
                            <InfoIcon className="w-5 h-5 text-primaryColor mr-2" />
                            <span className="font-semibold text-gray-900">Learn How Product Intelligence Works</span>
                        </div>
                        {isHowItWorksOpen ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                    </button>

                    <div
                        className={`
              grid transition-all duration-300 ease-in-out
              ${isHowItWorksOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}
            `}>
                        <div className="overflow-hidden">
                            <div className="p-6 space-y-6">
                                <div className="rounded-lg overflow-hidden relative">
                                    <img
                                        src={HowProductAnalyticsWork}
                                        alt="Tutorial preview"
                                        className="h-[70%] object-cover"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <h3 className="font-semibold text-gray-900">Developer Integration</h3>
                                        <p className="text-sm text-gray-600">
                                            Developers send telemetry events for users, accounts, and billing. Telemetry
                                            guidance and Event Studio verifications reduces setup time by 90%.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="font-semibold text-gray-900">Growth Team Customization</h3>
                                        <p className="text-sm text-gray-600">
                                            Growth and Product teams customize reports without technical skills. No SQL
                                            or data warehouse needed.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="font-semibold text-gray-900">Leadership Insights</h3>
                                        <p className="text-sm text-gray-600">
                                            Leadership teams access insights and set up alerts for key metrics and
                                            business outcomes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Setup Steps */}
                <div className="grid grid-cols-1 gap-3">
                    {setupCards.filter(card => card.isEnabled).map((card) => (
                        <SetupCard
                            key={card.id}
                            {...card}
                            isSelected={selectedCard === card.id}
                            isExpanded={expandedCard === card.id}
                            onSelect={handleSelect}
                            onExpand={handleExpand}
                            onInviteTeam={handleInviteTeam}
                        />
                    ))}
                </div>

                {/* Invite Modal */}
                {selectedTeam && showInviteModal && (
                    <InviteTeamModal
                        isOpen={showInviteModal}
                        onClose={() => setShowInviteModal(false)}
                        team={selectedTeam}
                        onInvite={() => { }}
                    />
                )}

                {selectedCard && (
                    <div className="fixed bottom-6 z-50" style={{ left: 'calc(50% + 40% - 50px)' }}>
                        <button
                            onClick={handleContinue}
                            disabled={
                                setupCards.find((card) => card.id === selectedCard)?.setupStatus === "coming_soon"
                            }
                            className={`
                inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors
                shadow-lg hover:shadow-xl
                ${setupCards.find((card) => card.id === selectedCard)?.setupStatus === "coming_soon"
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-primaryColor text-white hover:bg-primaryColor"
                                }
              `}>
                            <span>
                                {setupCards.find((card) => card.id === selectedCard)?.setupStatus === "coming_soon"
                                    ? "Planned"
                                    : "Continue Setup"}
                            </span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
